import type { OrganizationDto } from "../../common/service/server/dto/OrganizationDto";

export const PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP =
  "Set up your privacy email before contacting vendors.";

export const POSTMARK_INACTIVE_RECIPIENT_MESSAGE =
  "This email address has been marked inactive after a delivery failure. Please contact support for help reactivating it.";

interface PrivacyEmailInitializationInput {
  persistedMailbox?: string;
  savedMailbox: string;
  initialized: boolean;
  mailboxRequestSucceeded: boolean;
  rootDomain: string;
}

interface PrivacyEmailInitialization {
  email: string;
  persisted: boolean;
}

export function getPrivacyEmailInitialization({
  persistedMailbox,
  savedMailbox,
  initialized,
  mailboxRequestSucceeded,
  rootDomain,
}: PrivacyEmailInitializationInput): PrivacyEmailInitialization | null {
  const normalizedMailbox = persistedMailbox?.trim().toLowerCase();
  if (normalizedMailbox && (!initialized || normalizedMailbox !== savedMailbox)) {
    return { email: normalizedMailbox, persisted: true };
  }

  if (!initialized && mailboxRequestSucceeded && rootDomain) {
    return { email: `privacy@${rootDomain}`, persisted: false };
  }

  return null;
}

export function canSendPrivacyEmail(org?: OrganizationDto): boolean {
  return org?.mailbox?.canSendEmail === true;
}

export function isPrivacyEmailAndCustomDomainSetupComplete(org?: OrganizationDto): boolean {
  if (!org) {
    return false;
  }

  if (org.sendMethod === "POSTMARK") {
    if (org.postmarkMailboxMode === "SHARED") {
      return org.sharedPostmarkMailbox?.status === "VERIFIED";
    }

    const postmarkMailbox = org?.postmarkMailbox;
    return Boolean(
      org.domainVerifyComplete &&
        postmarkMailbox?.status === "VERIFIED" &&
        postmarkMailbox?.forwardingStatus === "CONFIRMED",
    );
  }

  if (!org?.domainVerifyComplete) {
    return false;
  }

  return org?.mailbox?.mailboxStatus === "OK";
}

export function shouldShowPrivacyEmailSetupCard(org?: OrganizationDto): boolean {
  return org?.sendMethod === "POSTMARK" && !isPrivacyEmailAndCustomDomainSetupComplete(org);
}

export function canUsePrivacyEmailInbox(org?: OrganizationDto): boolean {
  return (
    org?.sendMethod === "POSTMARK" &&
    org.postmarkMailboxMode === "OWNED" &&
    isPrivacyEmailAndCustomDomainSetupComplete(org)
  );
}
